/**
 * THE CAST CONTRACT — what makes a 36-character cast member loadable.
 *
 * ── Why this file is not `productionAssetManifest.ts` ───────────────────────
 *
 * The single shipped athlete is declared BY CONTENT: every byte hashed, every
 * triangle counted, frozen in a generated table. That is right for ONE asset
 * that changes once a quarter. It is wrong for a 36-model cast that four agents
 * are rebuilding while this loader is being written: a table of 144 SHA-256s
 * would be red on every run and would teach the next reader to ignore it.
 *
 * So the cast is gated on the CONTRACT instead — the properties that are locked
 * by `assets/player-master/A1_A2_CONTRACT.md` and cannot change without joint
 * sign-off, and which are true of every cast member regardless of who is
 * rebuilding his face this afternoon:
 *
 *   74 bones · root joint `root` · 12 `socket_*` · ≤4 influences · no clips in
 *   the mesh GLB · feet on y=0 · a four-level LOD ladder that gets cheaper.
 *
 * Nothing here names a mesh, a vertex count or a texture. A cast member that
 * satisfies this file loads; one that does not is REFUSED BY NAME, loudly, and
 * the squad slot he was going to fill says whose model is missing.
 *
 * ── The second list, and why it is separate ────────────────────────────────
 *
 * `CAST_SHIP_BUDGETS` is not the contract. It is the partitioning the RUNTIME
 * needs — one merged SkinnedMesh, one material, one texture set — which the
 * authored exports have never satisfied and are not supposed to: that is what
 * `pipeline/optimize_runtime.py` produces, downstream of the model build. A
 * cast member can be perfectly authored and still cost 20 draw calls, and those
 * are two different sentences that must never be collapsed into one boolean.
 * `castManifest.ts` reports both, and the game decides what to do with each:
 * a broken skeleton is refused, an unmerged mesh is drawn and counted.
 */

/** A2 locked contract, `A1_A2_CONTRACT.md` "Identity". */
export const CAST_BONE_COUNT = 74;
export const CAST_DEFORM_BONE_COUNT = 62;
export const CAST_SOCKET_COUNT = 12;
export const CAST_ROOT_BONE = 'root';
export const CAST_MAX_INFLUENCES = 4;
export const CAST_SOCKET_PREFIX = 'socket_';

/** The ladder every cast member ships. Level 0 is the closest/most detailed. */
export const CAST_LOD_LEVELS = [0, 1, 2, 3] as const;
export type CastLodLevel = (typeof CAST_LOD_LEVELS)[number];

/**
 * Squad heights are 1.73–1.92 m and the skeleton is SCALED at build time, so a
 * cast member's height is per-character data, not a constant. What is constant
 * is that the built model matches the height his config asked for. The pipeline
 * gate (`build_cast36.py`, `HEIGHT_TOL_M`) uses 2 mm on the armature; the
 * runtime measures the MESH bounding box instead, which legitimately differs
 * from the armature height by the scalp/boot shell, so the runtime tolerance is
 * deliberately looser and is a WARNING, never a refusal.
 */
export const CAST_HEIGHT_WARN_TOL_M = 0.14;

/** Stud tips touch y=0 exactly; the visual sole floats 4 mm (contract §Origin). */
export const CAST_SOLE_TOL_M = 0.02;

/**
 * What the runtime WANTS from a shipped cast member, as opposed to what the
 * contract REQUIRES of an authored one. Violations are counted and reported,
 * not thrown: the game draws the character and the manifest says what he costs.
 */
export interface CastShipBudget {
  readonly id: string;
  readonly limit: number;
  readonly why: string;
}

export const CAST_SHIP_BUDGETS: readonly CastShipBudget[] = [
  {
    id: 'meshes',
    limit: 1,
    why:
      'one merged SkinnedMesh per character. The authored export is 11-13 meshes / ' +
      '17-19 materials, i.e. ~20 draw calls per player — 22 players is then ~440 draws ' +
      'against a whole-scene budget that runs at 67 today. `optimize_runtime.py` is the ' +
      'pass that merges and atlases; until it has been run for a cast member, he is ' +
      'authored-correct and NOT shippable.',
  },
  {
    id: 'materials',
    limit: 1,
    why: 'one material per character, so a player is one program and one bind.',
  },
  {
    id: 'textures',
    limit: 1,
    why:
      'one atlas per character. Distinct characters cannot share a texture the way ' +
      'they share the master atlas today, so texture COUNT is what turns into VRAM: ' +
      'one 2048² RGBA atlas per character is 16 MB before mips, 21 MB with them.',
  },
];

/** Everything wrong with one cast member, collected rather than thrown one at a time. */
export interface CastFault {
  readonly id: string;
  readonly lod: number;
  /** `contract` refuses the model. `budget` draws it and counts the cost. */
  readonly kind: 'contract' | 'budget';
  readonly detail: string;
}

/** The measurements a loader/freezer takes off one parsed cast GLB. */
export interface CastLodMeasurement {
  readonly meshes: number;
  readonly primitives: number;
  readonly materials: number;
  readonly textures: number;
  readonly triangles: number;
  readonly bones: number;
  readonly rootBone: string | null;
  readonly sockets: number;
  readonly maxInfluences: number;
  readonly animations: number;
  readonly heightM: number;
  readonly soleM: number;
  /**
   * Rest world height of the `hips` bone, metres. The correction for the one
   * absolute translation track the clip pack keeps — see
   * AUTHORED_MASTER_HIPS_REST_Y. Optional because the node-side freezer
   * measures glTF JSON and predates this field.
   */
  readonly hipsRestY?: number;
}

/**
 * The one gate. Pure over a measurement so the freezer (node, glTF JSON) and the
 * loader (browser, three.js) reach the SAME verdict from the same code — a
 * second opinion written twice is how a cast member passes the build and fails
 * the game.
 */
export function checkCastLod(
  id: string,
  lod: number,
  m: CastLodMeasurement,
  declaredHeightM: number | null,
): CastFault[] {
  const faults: CastFault[] = [];
  const contract = (detail: string): void => {
    faults.push({ id, lod, kind: 'contract', detail });
  };
  const budget = (detail: string): void => {
    faults.push({ id, lod, kind: 'budget', detail });
  };

  if (m.bones !== CAST_BONE_COUNT) {
    contract(
      `${m.bones} joints; the A2 contract is ${CAST_BONE_COUNT} ` +
        `(${CAST_DEFORM_BONE_COUNT} deform + ${CAST_SOCKET_COUNT} socket_*). The clip pack is ` +
        `retargeted onto exactly this rig offline, so a different joint count resolves no ` +
        `tracks and the character stands in bind pose forever.`,
    );
  }
  if (m.rootBone !== null && m.rootBone !== CAST_ROOT_BONE) {
    contract(`root joint is "${m.rootBone}", the contract is "${CAST_ROOT_BONE}"`);
  }
  if (m.sockets !== CAST_SOCKET_COUNT) {
    contract(
      `${m.sockets} socket_* nodes; the contract is ${CAST_SOCKET_COUNT}. The sim reads ` +
        `contact points off these, so a missing one silently moves a strike point.`,
    );
  }
  if (m.maxInfluences > CAST_MAX_INFLUENCES) {
    contract(`${m.maxInfluences} skin influences; the contract caps at ${CAST_MAX_INFLUENCES}`);
  }
  if (m.animations > 0) {
    contract(
      `the mesh GLB carries ${m.animations} clips; a cast mesh must carry none. Clips come ` +
        `from the twelve M_*.glb files keyed on file stem — merging them into the mesh ` +
        `collapses all twelve onto one entry named "Animation".`,
    );
  }
  if (m.triangles <= 0) {
    contract('no triangles — this is not a character');
  }
  if (Math.abs(m.soleM) > CAST_SOLE_TOL_M) {
    contract(
      `sole at y=${m.soleM.toFixed(4)} m; the contract puts stud tips on y=0 exactly ` +
        `(±${CAST_SOLE_TOL_M} m) so the model is placed at pitch height with NO offset`,
    );
  }
  if (declaredHeightM !== null && Math.abs(m.heightM - declaredHeightM) > CAST_HEIGHT_WARN_TOL_M) {
    budget(
      `mesh height ${m.heightM.toFixed(4)} m vs the ${declaredHeightM} m his variant config ` +
        `declares — beyond the ${CAST_HEIGHT_WARN_TOL_M} m runtime tolerance`,
    );
  }

  for (const b of CAST_SHIP_BUDGETS) {
    const got =
      b.id === 'meshes' ? m.meshes : b.id === 'materials' ? m.materials : m.textures;
    if (got > b.limit) budget(`${got} ${b.id}, ship budget is ${b.limit} — ${b.why}`);
  }
  return faults;
}

/** Only contract faults refuse a model. Budget faults are drawn and counted. */
export function refuses(faults: readonly CastFault[]): boolean {
  return faults.some((f) => f.kind === 'contract');
}
